import React from 'react';
import {
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Button, 
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Local imports
import { useFormListLogic } from './hooks/useFormListLogic';
import FormCard from './components/FormCard';
import ShareDialog from './components/ShareDialog';
import { exportToExcel, exportToPDF } from './utils/exportUtils';
import './FormList.css';

/**
 * FormList ana bileşeni - Modüler yapıya dönüştürülmüş
 */
const FormListModular = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // Custom hook'tan tüm state ve fonksiyonları al
  const {
    // State
    filteredForms,
    loading,
    searchTerm,
    statusFilter,
    sortOrder,
    selectedForm,
    publicShareDialog,
    menuAnchorEl,
    selectedFormForMenu,
    
    // Setters
    setSearchTerm,
    setStatusFilter,
    setSortOrder,
    setPublicShareDialog,
    
    // Actions
    toggleFormStatus,
    deleteForm,
    copyForm,
    archiveForm,
    generatePublicToken,
    shareForm,
    handleMenuClick,
    handleMenuClose
  } = useFormListLogic();

  // Loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="form-container">
      <Paper className="form-paper" elevation={2}>
        {/* Üst Başlık ve Yeni Form Butonu */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {t('my_forms')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />} 
              onClick={() => navigate('/form-builder')}
            >
              {t('new_form')}
            </Button>
          </Box>
        </Box>

        {/* Arama ve Filtreleme Araçları */}
        <Box className="search-filter-container" sx={{ mb: 3 }}>
          <Box className="search-box">
            <TextField
              size="small"
              placeholder={t('search_form')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 200 }}
            />
          </Box>
          
          <Box className="filter-sort-box">
            <FormControl size="small" sx={{ minWidth: 120, mr: 1 }}>
              <InputLabel>{t('status')}</InputLabel>
              <Select
                value={statusFilter}
                label={t('status')}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">{t('all')}</MenuItem>
                <MenuItem value="active">{t('active')}</MenuItem>
                <MenuItem value="passive">{t('passive')}</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>{t('sort')}</InputLabel>
              <Select
                value={sortOrder}
                label={t('sort')}
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <MenuItem value="newest">{t('newest')}</MenuItem>
                <MenuItem value="oldest">{t('oldest')}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Form Kartları */}
        <Grid container spacing={2}>
          {filteredForms.length === 0 ? (
            <Grid item xs={12}>
              <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                minHeight="200px"
                flexDirection="column"
              >
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Henüz form bulunamadı
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Yeni bir form oluşturmak için "Yeni Form" butonunu kullanın.
                </Typography>
                <Button 
                  variant="contained" 
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/form-builder')}
                >
                  İlk Formunuzu Oluşturun
                </Button>
              </Box>
            </Grid>
          ) : (
            filteredForms.map((form) => (
              <Grid item xs={12} key={form.id}>
                <FormCard
                  form={form}
                  onEdit={(formId) => navigate(`/form-edit/${formId}`)}
                  onView={(formId) => navigate(`/form/${formId}`)}
                  onShare={shareForm}
                  onGeneratePublicToken={generatePublicToken}
                  onCopy={copyForm}
                  onArchive={archiveForm}
                  onDelete={deleteForm}
                  onToggleStatus={toggleFormStatus}
                  menuAnchorEl={menuAnchorEl}
                  selectedFormForMenu={selectedFormForMenu}
                  onMenuClick={handleMenuClick}
                  onMenuClose={handleMenuClose}
                />
              </Grid>
            ))
          )}
        </Grid>
      </Paper>

      {/* Share Dialog */}
      <ShareDialog 
        open={publicShareDialog}
        onClose={() => setPublicShareDialog(false)}
        selectedForm={selectedForm}
      />
    </Box>
  );
};

export default FormListModular;
